import { FieldState, FormState } from 'formstate';
import { action, computed, makeObservable, observable, ObservableMap, runInAction } from 'mobx';
import {
    PartitionInput,
    PartitionConfig,
    IPartitionDetail,
    TenantManagementApi,
} from '@servicetitan/marketing-tenant-management-api';
import { inject, injectable } from '@servicetitan/react-ioc';
import {
    formStateToJS,
    InputFieldState,
    setFormStateValues,
    commitFormState,
} from '@servicetitan/form-state';
import {
    DEFAULT_CONFIGURATION_VALIDATORS,
    REQUIRED_CONFIGURATION_VALIDATORS,
} from '../utils/validators';
import { LoadStatus } from '../../common/enums/load-status';
import { withTrimmedValues } from '../utils/with-trimmed-values';
import { sortFeatureGates } from '../utils/sorters';
import { ManageTenantStore } from './manage-tenant.store';

@injectable()
export class ManagePartitionStore {
    formState = new FormState({
        displayName: new InputFieldState('').validators(...REQUIRED_CONFIGURATION_VALIDATORS),
        emailScope: new InputFieldState('').validators(...DEFAULT_CONFIGURATION_VALIDATORS),
        emailSenderDomain: new InputFieldState('').validators(...DEFAULT_CONFIGURATION_VALIDATORS),
        directMailProvider: new InputFieldState('').validators(...DEFAULT_CONFIGURATION_VALIDATORS),
        emailTemplateOptOut: new FieldState<boolean>(false),
        authPath: new InputFieldState('').validators(...DEFAULT_CONFIGURATION_VALIDATORS),
    });

    @observable partition?: IPartitionDetail;
    @observable pageLoadStatus = LoadStatus.None;
    @observable saveLoadStatus = LoadStatus.None;

    partnerGenericConfigs: ObservableMap<string> = observable.map([], { deep: false });
    tenantGenericConfigs: ObservableMap<string> = observable.map([], { deep: false });
    partitionGenericConfigs: ObservableMap<string> = observable.map([], { deep: false });

    @computed get tenantName() {
        return this.partition?.tenantDetails?.displayName;
    }

    @computed get isFormChanged() {
        return (
            this.formState.$.displayName.dirty ||
            this.formState.$.emailScope.dirty ||
            this.formState.$.emailSenderDomain.dirty ||
            this.formState.$.directMailProvider.dirty ||
            this.formState.$.emailTemplateOptOut.dirty ||
            this.formState.$.authPath.dirty
        );
    }

    constructor(
        @inject(TenantManagementApi) private readonly tenantManagementApi: TenantManagementApi,
        @inject(ManageTenantStore) private readonly manageTenantStore: ManageTenantStore,
    ) {
        makeObservable(this);
    }

    init = async (
        externalPartnerId: string,
        externalTenantId: string,
        externalPartitionId: string,
    ) => {
        this.formState.disableAutoValidation();
        this.manageTenantStore.setupShowTelecom(externalPartnerId);

        runInAction(() => {
            this.pageLoadStatus = LoadStatus.Loading;
        });

        try {
            const { data: partition } = await this.tenantManagementApi.partitionGET(
                externalPartnerId,
                externalTenantId,
                externalPartitionId,
            );

            const {
                displayName,
                tenantDetails: {
                    configs: { generic: tenantGenericConfigs },
                    partnerDetail: {
                        configs: { generic: partnerGenericConfigs },
                    },
                },
                configs: {
                    emailSenderDomain,
                    emailScope,
                    directMailProvider,
                    emailTemplateOptOut,
                    authPath,
                    generic: partitionGenericConfigs,
                },
            } = partition;

            setFormStateValues(this.formState, {
                displayName,
                emailScope,
                emailSenderDomain,
                directMailProvider,
                emailTemplateOptOut: emailTemplateOptOut ?? false,
                authPath: authPath || '',
            });

            commitFormState(this.formState);

            runInAction(() => {
                this.partition = partition;
                this.partnerGenericConfigs.replace(sortFeatureGates(partnerGenericConfigs));
                this.tenantGenericConfigs.replace(sortFeatureGates(tenantGenericConfigs));
                this.partitionGenericConfigs.replace(sortFeatureGates(partitionGenericConfigs));
                this.pageLoadStatus = LoadStatus.Ok;
            });
        } catch {
            runInAction(() => {
                this.pageLoadStatus = LoadStatus.Error;
            });
        }
    };

    saveConfigs = async () => {
        if (!this.partition || this.saveLoadStatus === LoadStatus.Loading) {
            return;
        }

        try {
            await this.formState.enableAutoValidationAndValidate();

            if (this.formState.hasError) {
                return;
            }

            runInAction(() => {
                this.saveLoadStatus = LoadStatus.Loading;
            });

            const {
                displayName,
                directMailProvider,
                emailSenderDomain,
                emailScope,
                emailTemplateOptOut,
                authPath,
            } = formStateToJS(this.formState);

            const partitionConfig = new PartitionConfig({
                ...this.partition.configs,
                emailScope,
                emailSenderDomain,
                directMailProvider,
                emailTemplateOptOut,
                authPath,
                generic: {}, // generic configs are not saved by this BE method
            });

            const partitionDetails = new PartitionInput({
                displayName,
                configs: partitionConfig,
            });

            const body = withTrimmedValues(partitionDetails);

            await this.tenantManagementApi.configPUT(this.partition.internalId, body);

            if (this.partition.displayName !== body.displayName) {
                this.partition.displayName = body.displayName;
            }

            commitFormState(this.formState);

            runInAction(() => {
                this.saveLoadStatus = LoadStatus.Ok;
            });
        } catch {
            runInAction(() => {
                this.saveLoadStatus = LoadStatus.Error;
            });
        }
    };

    @action resetConfiguration = () => {
        this.formState.reset();
        this.saveLoadStatus = LoadStatus.None;
    };
}
